import { supabase } from '../utils/supabase';

/**
 * Service for handling user ratings and feedback
 */
export const ratingService = {
  /**
   * Calculate and update a user's average rating based on all their feedback
   * @param {string} userId - The user ID to calculate rating for
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async recalculateUserRating(userId) {
    try {
      // Get all feedback for the user
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('user_feedback')
        .select('rating')
        .eq('user_id', userId)
        .not('rating', 'is', null);

      if (feedbackError) throw feedbackError;

      // Calculate averages
      let averageRating = 0;
      let totalReviews = 0;

      if (feedbackData && feedbackData.length > 0) {
        const totalRating = feedbackData.reduce((sum, feedback) => sum + feedback.rating, 0);
        averageRating = totalRating / feedbackData.length;
        totalReviews = feedbackData.length;
      }

      // Update user profile with calculated rating
      const { data: updateData, error: updateError } = await supabase
        .from('user_profiles')
        .update({
          rating: averageRating,
          total_reviews: totalReviews,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select('rating, total_reviews');

      if (updateError) throw updateError;

      return {
        success: true,
        data: {
          userId,
          rating: averageRating,
          totalReviews,
          updated: updateData[0]
        }
      };

    } catch (error) {
      console.error('Error recalculating user rating:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Add feedback for a user and automatically recalculate their rating
   * @param {Object} feedbackData - The feedback data
   * @param {string} feedbackData.user_id - User receiving feedback
   * @param {string} feedbackData.employer_id - User giving feedback  
   * @param {number} feedbackData.rating - Rating (1-5)
   * @param {string} feedbackData.comment - Optional comment
   * @param {string} feedbackData.job_id - Optional job ID
   * @param {string} feedbackData.application_id - Optional application ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async addFeedback(feedbackData) {
    try {
      // Validate rating
      if (!feedbackData.rating || feedbackData.rating < 1 || feedbackData.rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      // Insert feedback
      const { data: insertData, error: insertError } = await supabase
        .from('user_feedback')
        .insert([{
          user_id: feedbackData.user_id,
          employer_id: feedbackData.employer_id,
          rating: feedbackData.rating,
          comment: feedbackData.comment || null,
          job_id: feedbackData.job_id || null,
          application_id: feedbackData.application_id || null,
          created_at: new Date().toISOString()
        }])
        .select();

      if (insertError) throw insertError;

      // Recalculate the user's rating
      const recalcResult = await this.recalculateUserRating(feedbackData.user_id);
      
      return {
        success: true,
        data: {
          feedback: insertData[0],
          ratingUpdate: recalcResult.data
        }
      };

    } catch (error) {
      console.error('Error adding feedback:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get detailed rating breakdown for a user
   * @param {string} userId - The user ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getRatingBreakdown(userId) {
    try {
      // Get user profile with ratings
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('rating, total_reviews, communication_rating, work_quality_rating')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Get all feedback with employer details
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('user_feedback')
        .select(`
          rating,
          comment,
          created_at,
          employer_id,
          user_profiles!user_feedback_employer_id_fkey(full_name)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (feedbackError) throw feedbackError;

      // Calculate distribution
      const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      feedbackData.forEach(feedback => {
        if (feedback.rating >= 1 && feedback.rating <= 5) {
          distribution[feedback.rating]++;
        }
      });

      return {
        success: true,
        data: {
          profile: profileData,
          feedback: feedbackData,
          distribution,
          recentReviews: feedbackData.slice(0, 5)
        }
      };

    } catch (error) {
      console.error('Error getting rating breakdown:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Get applicant rating and review count by applicant ID
   * @param {string} applicantId - The applicant's user profile ID
   * @returns {Promise<{success: boolean, data?: {rating: number, totalReviews: number}, error?: string}>}
   */
  async getApplicantRating(applicantId) {
    try {
      if (!applicantId) {
        throw new Error('Applicant ID is required');
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('rating, total_reviews')
        .eq('id', applicantId)
        .single();

      if (error) {
        // Check if it's a "not found" error vs other errors
        if (error.code === 'PGRST116') {
          // Row not found - return defaults
          return {
            success: true,
            data: {
              rating: 0,
              totalReviews: 0
            }
          };
        }
        throw error;
      }

      // Sanitize data and provide defaults
      return {
        success: true,
        data: {
          rating: data?.rating || 0,
          totalReviews: data?.total_reviews || 0
        }
      };

    } catch (error) {
      console.error(`Error fetching applicant rating for ${applicantId}:`, error);
      return {
        success: false,
        error: error.message || 'Failed to fetch applicant rating'
      };
    }
  },

  /**
   * Recalculate ratings for all users (admin function)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async recalculateAllRatings() {
    try {
      // Get all users with feedback
      const { data: usersWithFeedback, error: usersError } = await supabase
        .from('user_feedback')
        .select('user_id')
        .not('user_id', 'is', null);

      if (usersError) throw usersError;

      // Get unique user IDs
      const uniqueUserIds = [...new Set(usersWithFeedback.map(f => f.user_id))];
      
      const results = [];
      
      // Recalculate for each user
      for (const userId of uniqueUserIds) {
        const result = await this.recalculateUserRating(userId);
        results.push(result);
      }

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      return {
        success: true,
        data: {
          total: uniqueUserIds.length,
          successful,
          failed,
          results
        }
      };

    } catch (error) {
      console.error('Error recalculating all ratings:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default ratingService;